import type { WorkModelView } from "@/lib/workModel";
import { useState } from "react";
import { Star } from "lucide-react";

import { cn } from "@/lib/utils";
import GenerativeContour from "@/components/primitives/GenerativeContour";
import CoverTexture from "@/components/primitives/CoverTexture";
import { resolveTeamSizeBand } from "@/lib/teamSize";

/**
 * Shared building blocks for the Explore redesign. Every piece is built for the
 * REAL platform state — most orgs unscored, many with no reviews, some with no
 * team size / service — and is designed to look just as finished as a fully
 * populated card. Visuals use the canonical primitives (GenerativeContour,
 * FitScoreRing) and the design tokens; nothing here re-derives them.
 */

/** Shape the Explore views read — a subset of the page's mapped Organization. */
export interface ExploreOrg {
  id: string;
  name: string;
  slug: string;
  type?: string;
  location: string;
  heroImage?: string;
  /** profile_data.coverImagePosition — the authored cover focal point (0–100). */
  coverImagePosition?: { x: number; y: number } | null;
  logo?: string;
  rating: number;
  reviewCount: number;
  highlights?: { split?: string; agents?: string; focus?: string };
  /** profile_data.agentCount — used to derive the RealTrends band label. */
  agentCount?: number | null;
  serviceTags?: string[];
  /** The cloud label as shown (src/lib/workModel.ts); absent reads as no badge. */
  workModel?: WorkModelView;
  /** A basic listing (#1039): no fit score, "Not enough information to score" (F4). */
  listed?: boolean;
  /** Shown only because it shares a market with an in-city match (#1190): carries a "Nearby" badge. */
  nearby?: boolean;
}

const PLACEHOLDER = "/placeholder.svg";

/** A url is a real, usable image — not empty and not the placeholder asset. */
export const hasRealImage = (url?: string | null): boolean =>
  !!url && url.trim() !== "" && !url.includes(PLACEHOLDER);

/** org_type enum → human label. Unknown values are title-cased. */
export const typeLabel = (type?: string): string => {
  if (!type) return "Organization";
  const map: Record<string, string> = {
    team: "Team",
    brokerage: "Brokerage",
    franchise: "Franchise",
    corporate: "Corporate",
    independent: "Independent",
    boutique: "Boutique",
  };
  return map[type] ?? type.charAt(0).toUpperCase() + type.slice(1);
};

/** "Team · Austin, TX" — drops the separator when either half is missing. */
export const typeCityLine = (org: ExploreOrg): string =>
  [typeLabel(org.type), org.location].filter(Boolean).join(" · ");

/** 1–2 letter monogram from the org name (initials of the first two words). */
export const monogramFor = (name: string): string => {
  const words = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "•";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

/**
 * Team-size + service tags. Uses the RealTrends band label derived from
 * agentCount (e.g. "Large · 14 agents"). Empties simply drop out — never a
 * bare dash.
 */
export const metaTags = (org: ExploreOrg): string[] => {
  const tags: string[] = [];
  const band = resolveTeamSizeBand(org.agentCount);
  if (band) {
    const count = org.agentCount!;
    tags.push(`${band.label} · ${count} agent${count === 1 ? "" : "s"}`);
  }
  (org.serviceTags ?? []).slice(0, 2).forEach((s) => {
    if (s && s.trim()) tags.push(s);
  });
  return tags;
};

/**
 * "Best fit" ordering: scored orgs by descending score, then not-yet-scored
 * orgs, then listed orgs (a null score is never treated as 0). A listed org
 * (decision F4, #1041) has no score whatever the resolver says, so it can never
 * rank above a scored one. Stable within groups.
 */
export const bestFitSort = <T extends { id: string; listed?: boolean }>(
  orgs: T[],
  resolveFitScore?: (orgId: string) => number | null | undefined,
): T[] => {
  const score = (o: T) => {
    if (o.listed === true) return null;
    const s = resolveFitScore?.(o.id);
    return typeof s === "number" ? s : null;
  };
  // 0 scored, 1 not yet scored, 2 listed.
  const group = (o: T, s: number | null) => (s != null ? 0 : o.listed === true ? 2 : 1);
  return orgs
    .map((o, i) => {
      const s = score(o);
      return { o, i, s, g: group(o, s) };
    })
    .sort((a, b) => {
      if (a.g !== b.g) return a.g - b.g;
      if (a.s == null || b.s == null) return a.i - b.i;
      return b.s - a.s || a.i - b.i;
    })
    .map((x) => x.o);
};

/* ──────────────────────────── Rating ──────────────────────────── */

export function ExploreRating({
  org,
  size = 13,
}: {
  org: ExploreOrg;
  size?: number;
}) {
  const star = Math.round(size + 1);
  if (!org.reviewCount) {
    // No reviews → an honest muted line with an outline star — never "0 (0 reviews)".
    return (
      <div className="flex items-center gap-1.5 text-muted-foreground" style={{ fontSize: size }}>
        <Star style={{ width: star, height: star }} className="shrink-0" strokeWidth={1.7} />
        <span>No reviews yet</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5" style={{ fontSize: size }}>
      <Star
        style={{ width: star, height: star }}
        className="shrink-0 fill-gold-highlight text-gold-highlight"
        strokeWidth={1.7}
      />
      <span className="font-bold tabular-nums text-ink-900">{org.rating.toFixed(1)}</span>
      <span className="tabular-nums text-ink-500">
        · {org.reviewCount} review{org.reviewCount === 1 ? "" : "s"}
      </span>
    </div>
  );
}

/* ──────────────────────────── Tags ──────────────────────────── */

export function ExploreTags({
  org,
  variant = "card",
}: {
  org: ExploreOrg;
  variant?: "card" | "list";
}) {
  const tags = metaTags(org);
  if (!tags.length) {
    // No tags in either variant → render nothing (agent-facing views never show
    // a "not shared" or dash placeholder; the block is simply omitted).
    return null;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {tags.map((t) => (
        <span
          key={t}
          className={cn(
            "whitespace-nowrap rounded-full border border-border bg-card font-medium text-ink-500",
            variant === "list" ? "px-2.5 py-0.5 text-[11.5px]" : "px-2.5 py-1 text-xs",
          )}
        >
          {t}
        </span>
      ))}
    </div>
  );
}

/* ──────────────────────────── Cover field ──────────────────────────── */

const NEUTRAL_MONOGRAM_SIZE = {
  sm: "text-[15px]",
  md: "text-[22px]",
} as const;

/**
 * The bright UI's stand-in for the contour art: a teal-tint tile, optionally
 * with the monogram in teal (design system v3: a letter avatar on tint).
 */
export function NeutralMonogram({
  name,
  className,
  size = "md",
  showGlyph = true,
  textured = false,
}: {
  name: string;
  className?: string;
  /** "sm" = the 46px OrgHeaderCell glyph tile; "md" (default) = the cover-field size. */
  size?: "sm" | "md";
  /** Hide the monogram glyph, leaving only the muted tile (e.g. behind a logo chip). */
  showGlyph?: boolean;
  /** Add the org's quiet contour-or-dots texture behind the glyph (cover fields only). */
  textured?: boolean;
}) {
  return (
    <div className={cn("flex h-full w-full items-center justify-center bg-primary-tint text-primary", className)} aria-hidden>
      {textured && <CoverTexture seed={name} />}
      {showGlyph && (
        <span className={cn("relative font-heading font-bold tracking-tight text-primary", NEUTRAL_MONOGRAM_SIZE[size])}>
          {monogramFor(name)}
        </span>
      )}
    </div>
  );
}

/**
 * The cover field used behind every card / thumb. Resolution order:
 *   1. a real uploaded cover photo (falls back to the contour on load error);
 *   2. no photo but a real logo  → contour + a white logo chip;
 *   3. neither                   → contour + the org monogram.
 * Never a broken or duplicate stock image.
 */
export function ExploreCoverField({
  org,
  rings,
  className,
  withLogoChip = true,
  neutral = false,
}: {
  org: ExploreOrg;
  rings?: number;
  className?: string;
  /** Show a white logo chip when a real logo exists but no cover photo does. */
  withLogoChip?: boolean;
  /** Bright UI: a neutral tile instead of the contour art behind a photo, logo chip or monogram. */
  neutral?: boolean;
}) {
  const [imgError, setImgError] = useState(false);
  // Only the neutral (bright UI) path uses this: on the default path a failed
  // logo just hides its own <img> via inline style, leaving the contour art
  // behind it — never a blank tile — so tracking logo failure there would
  // change default behaviour for no reason. Under `neutral` there is no art
  // behind the chip, so a failed logo must fall back to the monogram instead.
  const [logoError, setLogoError] = useState(false);
  const showPhoto = hasRealImage(org.heroImage) && !imgError;
  const showLogoChip =
    withLogoChip && !showPhoto && hasRealImage(org.logo) && !(neutral && logoError);
  const variant = showPhoto || showLogoChip ? "cover" : "logo";
  // Same focal-point resolution the org profile header uses, so every surface
  // frames the authored cover identically.
  const coverPos = org.coverImagePosition
    ? `${org.coverImagePosition.x}% ${org.coverImagePosition.y}%`
    : "center";

  return (
    <div className={cn("relative h-full w-full overflow-hidden", className)}>
      {neutral ? (
        // The glyph is the fallback for an org with no image. Behind a cover
        // photo it would show through a transparent PNG, so it is not drawn.
        <NeutralMonogram
          name={org.name}
          className="absolute inset-0"
          showGlyph={!showPhoto && !showLogoChip}
          textured={!showPhoto}
        />
      ) : (
        <GenerativeContour
          name={org.name}
          variant={variant}
          rings={rings}
          monogram={monogramFor(org.name)}
          className="absolute inset-0"
        />
      )}
      {showPhoto && (
        <img
          src={org.heroImage as string}
          alt={`${org.name} cover`}
          // bg-card: a transparent PNG sits on white, not on the tile or the
          // contour art behind it.
          className="absolute inset-0 h-full w-full bg-card object-cover"
          style={{ objectPosition: coverPos }}
          onError={() => setImgError(true)}
        />
      )}
      {showLogoChip && (
        <div className="absolute inset-0 flex items-center justify-center p-3">
          <div className="flex items-center justify-center rounded-lg bg-card px-4 py-2 shadow-card">
            <img
              src={org.logo as string}
              alt={`${org.name} logo`}
              className="max-h-7 max-w-[120px] object-contain"
              onError={(e) => {
                if (neutral) setLogoError(true);
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
